import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Psychology as SemanticIcon,
  Merge as HybridIcon,
  Search as LexicalIcon,
  AutoFixHigh as AutoIcon,
} from '@mui/icons-material';

const RetrievalMethodExplanation = ({ 
  compact = false, 
  showTooltip = true, 
  selectedMethod = null,
  tooltipOnly = false // Nova prop para modo mais clean
}) => {
  const [expanded, setExpanded] = useState(false);

  const methods = {
    semantic: {
      title: "Busca Semântica",
      icon: <SemanticIcon />,
      color: "primary",
      when: "Para conceitos e ideias",
      description: "Entende o significado das palavras e encontra conteúdo relacionado, mesmo que use palavras diferentes.",
      examples: [
        '"como escrever artigos" → encontra textos sobre redação e escrita',
        '"análise de dados" → encontra informações sobre estatística e relatórios'
      ],
      pros: ["Entende sinônimos", "Busca conceitual", "Flexível"],
      cons: ["Pode ser vaga para termos específicos"]
    },
    hybrid: {
      title: "Busca Híbrida (Recomendada)",
      icon: <HybridIcon />,
      color: "success",
      when: "Para a maioria dos casos",
      description: "Combina busca semântica + busca por palavras-chave. O sistema escolhe automaticamente a melhor estratégia baseado na sua pergunta.",
      examples: [
        '"ISO 9001" → usa palavras-chave para encontrar exatamente essa norma',
        '"como melhorar qualidade" → usa significado para encontrar explicações'
      ],
      pros: ["Melhor precisão", "Escolha automática inteligente", "Combina o melhor dos dois mundos"],
      cons: ["Processamento ligeiramente mais lento"],
      autoFeatures: [
        "Detecta automaticamente termos específicos (códigos, normas, leis)",
        "Ajusta parâmetros para otimizar resultados",
        "Balanceia precisão vs. abrangência"
      ]
    },
    lexical: {
      title: "Busca Lexical (BM25)",
      icon: <LexicalIcon />,
      color: "warning",
      when: "Para termos exatos",
      description: "Busca por palavras-chave exatas no texto. Ideal quando você sabe exatamente qual termo está procurando.",
      examples: [
        '"Python pandas" → encontra apenas textos que mencionam "pandas"',
        '"Artigo 5°" → encontra apenas esse artigo específico'
      ],
      pros: ["Busca exata", "Rápida", "Previsível"],
      cons: ["Não entende sinônimos", "Pode perder conteúdo relevante"]
    }
  };

  const TooltipContent = () => (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Como escolher o método de busca?
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Híbrida:</strong> Recomendada. Combina busca por significado + palavras exatas automaticamente.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Semântica:</strong> Para conceitos amplos e ideias relacionadas.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Lexical:</strong> Para encontrar termos específicos e exatos.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        <strong>Threshold:</strong> Score mínimo (0-1) para considerar um resultado relevante. Quanto maior, mais restritivo.
      </Typography>
    </Box>
  );

  // Modo tooltip-only (para páginas de edição e teste)
  if (tooltipOnly || (compact && showTooltip)) {
    return (
      <Tooltip title={<TooltipContent />} placement="right" arrow>
        <IconButton size="small">
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {showTooltip && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Tooltip title={<TooltipContent />} placement="right" arrow>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            Clique para entender as diferenças
          </Typography>
        </Box>
      )}

      <Accordion 
        expanded={expanded} 
        onChange={() => setExpanded(!expanded)}
        sx={{ 
          boxShadow: 'none', 
          border: 1, 
          borderColor: 'divider',
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" color="primary">
              Como funcionam os métodos de busca?
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(methods).map(([key, method]) => (
              <Box 
                key={key}
                sx={{ 
                  p: 2, 
                  border: 1, 
                  borderColor: selectedMethod === key ? `${method.color}.main` : 'divider',
                  borderRadius: 1,
                  bgcolor: selectedMethod === key ? `${method.color}.50` : 'transparent'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {method.icon}
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {method.title}
                  </Typography>
                  <Chip 
                    label={method.when} 
                    size="small" 
                    color={method.color}
                    variant="outlined"
                  />
                  {key === 'hybrid' && (
                    <Chip 
                      label="Recomendada" 
                      size="small" 
                      color="success"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>

                <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {method.description}
                </Typography>

                {/* Características especiais do híbrido */}
                {key === 'hybrid' && method.autoFeatures && (
                  <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                      Escolha Automática Inteligente:
                    </Typography>
                    {method.autoFeatures.map((feature, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', mb: 0.2 }}>
                        • {feature}
                      </Typography>
                    ))}
                  </Alert>
                )}

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Exemplos:
                  </Typography>
                  {method.examples.map((example, idx) => (
                    <Typography key={idx} variant="caption" sx={{ display: 'block', ml: 1, fontStyle: 'italic' }}>
                      • {example}
                    </Typography>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      Vantagens:
                    </Typography>
                    {method.pros.map((pro, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', ml: 1 }}>
                        • {pro}
                      </Typography>
                    ))}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      Limitações:
                    </Typography>
                    {method.cons.map((con, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', ml: 1 }}>
                        • {con}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />
            
            <Alert severity="success">
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Dica: Na dúvida, use <strong>Híbrida</strong>!
              </Typography>
              <Typography variant="caption">
                O sistema automaticamente detecta se sua pergunta precisa de busca exata (como "ISO 9001") 
                ou conceitual (como "como melhorar qualidade") e ajusta a estratégia para te dar os melhores resultados.
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default RetrievalMethodExplanation;